import React from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { getMsal } from "../factories/msalFactory";
import { useCustomManager } from "../../../hooks/useManagers";

const PBI_AUD = "https://analysis.windows.net/powerbi/api";

// ---------- helpers ----------
const base64UrlDecode = (s) => {
  try {
    const pad = "=".repeat((4 - (s.length % 4)) % 4);
    const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return {};
  }
};
const getJwtExp = (jwt) => {
  try {
    const [, payload] = jwt.split(".");
    const obj = base64UrlDecode(payload);
    return typeof obj?.exp === "number" ? obj.exp : 0;
  } catch {
    return 0;
  }
};
const nowEpoch = () => Math.floor(Date.now() / 1000);
const cacheKey = (reportId) => `pbi-embed-token:${reportId}`;

export default function EmbeddedReport({
  clientId,
  reportItemId,
  reportId,
  groupId,
  ctid,
  height = 820,
}) {
  const [accessToken, setAccessToken] = React.useState(null);
  const [tokenType, setTokenType] = React.useState(models.TokenType.Aad);
  const [embedUrl, setEmbedUrl] = React.useState(null);
  const [embedError, setEmbedError] = React.useState(null);

  const reportMgr = useCustomManager("Report");

  // Stable refs
  const mountedRef = React.useRef(true);
  const embedRef = React.useRef(null);
  const tokenTypeRef = React.useRef(models.TokenType.Aad);
  const reportMgrRef = React.useRef(reportMgr);

  React.useEffect(() => () => { mountedRef.current = false; }, []);
  React.useEffect(() => { tokenTypeRef.current = tokenType; }, [tokenType]);
  React.useEffect(() => { reportMgrRef.current = reportMgr; }, [reportMgr]);

  const pbiScopes = React.useMemo(() => [`${PBI_AUD}/.default`], []);
  const manualEmbedUrl = React.useMemo(
    () => `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${groupId}`,
    [reportId, groupId]
  );

  const saveToken = React.useCallback((token, type) => {
    const exp = getJwtExp(token);
    try {
      localStorage.setItem(cacheKey(reportId), JSON.stringify({ token, type, exp }));
    } catch {}
  }, [reportId]);

  const readCachedToken = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(cacheKey(reportId));
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj?.token || typeof obj?.type === "undefined") return null;
      const exp = Number(obj.exp || 0);
      if (exp && exp <= nowEpoch() + 30) return null; // small buffer
      return obj; // { token, type, exp }
    } catch {
      return null;
    }
  }, [reportId]);

  // Always use manual embed URL (works for AAD + Embed)
  const getEmbedUrl = React.useCallback(async () => manualEmbedUrl, [manualEmbedUrl]);

  // Stable MSAL flow
  const tryMsalFlow = React.useCallback(async () => {
    const msal = await getMsal(clientId, ctid);
    const request = { scopes: pbiScopes };
    try {
      const r1 = await msal.acquireTokenSilent(request);
      return r1.accessToken;
    } catch {
      const r2 = await msal.acquireTokenPopup({ ...request, prompt: "select_account" });
      return r2.accessToken;
    }
  }, [clientId, ctid, pbiScopes]);

  // Stable embed-token fetch that doesn't depend on a changing manager reference
  const getFreshEmbedToken = React.useCallback(async (rid) => {
    const mgr = reportMgrRef.current;
    const res = await mgr.getPBIToken(rid);
    const token = res?.Success ? res?.Value : null;
    return token || null;
  }, []);

  // Boot exactly when the identifiers change
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) cached token?
        const cached = readCachedToken();
        if (cached) {
          const url = await getEmbedUrl();
          if (cancelled || !mountedRef.current) return;

          setTokenType((prev) => (prev === cached.type ? prev : cached.type));
          setAccessToken((prev) => (prev === cached.token ? prev : cached.token));
          setEmbedUrl((prev) => (prev === url ? prev : url));
          return;
        }

        // 2) backend embed token?
        const embedToken = await getFreshEmbedToken(reportItemId);
        if (embedToken) {
          const url = await getEmbedUrl();
          if (cancelled || !mountedRef.current) return;

          setTokenType((prev) => (prev === models.TokenType.Embed ? prev : models.TokenType.Embed));
          setAccessToken((prev) => (prev === embedToken ? prev : embedToken));
          setEmbedUrl((prev) => (prev === url ? prev : url));
          saveToken(embedToken, models.TokenType.Embed);
          return;
        }

        // 3) AAD (user owns data)
        const aad = await tryMsalFlow();
        const url = await getEmbedUrl();
        if (cancelled || !mountedRef.current) return;

        setTokenType((prev) => (prev === models.TokenType.Aad ? prev : models.TokenType.Aad));
        setAccessToken((prev) => (prev === aad ? prev : aad));
        setEmbedUrl((prev) => (prev === url ? prev : url));
        saveToken(aad, models.TokenType.Aad);
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        setEmbedError({ message: "Initialization failed.", detail: err?.message || String(err) });
      }
    })();

    return () => { cancelled = true; };
  }, [reportId, groupId, reportItemId, clientId, ctid, readCachedToken, getEmbedUrl, getFreshEmbedToken, tryMsalFlow, saveToken]);

  // Stable event handlers (no dependency churn)
  const tryMsalFlowRef = React.useRef(tryMsalFlow);
  const getFreshEmbedTokenRef = React.useRef(getFreshEmbedToken);
  React.useEffect(() => { tryMsalFlowRef.current = tryMsalFlow; }, [tryMsalFlow]);
  React.useEffect(() => { getFreshEmbedTokenRef.current = getFreshEmbedToken; }, [getFreshEmbedToken]);

  const eventHandlers = React.useMemo(() => {
    const map = new Map();

    map.set("loaded", () => {});
    map.set("rendered", () => {});

    map.set("tokenExpired", async () => {
      try {
        let newToken = null;
        if (tokenTypeRef.current === models.TokenType.Embed) {
          newToken = await getFreshEmbedTokenRef.current(reportItemId);
        } else {
          newToken = await tryMsalFlowRef.current();
        }
        if (!newToken) throw new Error("Could not refresh token");

        // Update token on the existing embed only (no re-embed)
        embedRef.current?.setAccessToken?.(newToken);
        setAccessToken((prev) => (prev === newToken ? prev : newToken));
        saveToken(newToken, tokenTypeRef.current);
      } catch (ex) {
        setEmbedError({ message: "Token refresh failed", detail: ex?.message || String(ex) });
      }
    });

    map.set("error", async (e) => {
      const m = (e?.detail?.message || e?.detail?.error || String(e?.detail || e) || "").toLowerCase();
      const looksAuth = m.includes("unauthorized") || m.includes("forbidden") || m.includes("token") || m.includes("resolve cluster");
      if (looksAuth) {
        try {
          const aad = await tryMsalFlowRef.current();
          const url = await getEmbedUrl();
          if (!mountedRef.current) return;
          setTokenType((prev) => (prev === models.TokenType.Aad ? prev : models.TokenType.Aad));
          setAccessToken((prev) => (prev === aad ? prev : aad));
          setEmbedUrl((prev) => (prev === url ? prev : url));
          saveToken(aad, models.TokenType.Aad);
          return;
        } catch (ex) {
          setEmbedError({ message: "Embed retry failed", detail: ex?.message || String(ex) });
          return;
        }
      }
      setEmbedError({ message: "Embed failed", detail: e?.detail?.message || String(e?.detail || e) });
    });

    return map;
  }, [reportItemId, getEmbedUrl, saveToken]);

  // Memoize embedConfig so its identity is stable
  const embedConfig = React.useMemo(() => {
    if (!accessToken || !embedUrl) return null;
    return {
      type: "report",
      id: reportId,
      embedUrl: embedUrl || manualEmbedUrl,
      tokenType,
      accessToken,
      settings: {
        panes: { filters: { visible: true, expanded: false }, pageNavigation: { visible: true } },
      },
    };
  }, [reportId, embedUrl, manualEmbedUrl, tokenType, accessToken]);

  if (embedError) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>We couldn’t load this report.</div>
        <div style={{ opacity: 0.8, marginBottom: 8 }}>{embedError.message}</div>
        {embedError.detail && <div style={{ fontFamily: "monospace" }}>{embedError.detail}</div>}
      </div>
    );
  }
  if (!embedConfig) return null;

  return (
    <PowerBIEmbed
      embedConfig={embedConfig}
      getEmbeddedComponent={(embedObject) => { embedRef.current = embedObject; }}
      cssClassName="pbiEmbed"
      eventHandlers={eventHandlers}
      style={{ height }}
    />
  );
}
